import { hasPermission } from '@app/shared'
import { useQuery } from '@tanstack/react-query'

import { getCurrentUser, getMasters, getSettings } from '../api/admin'
import { ApiError } from './http'
import { isAuthenticated } from './session'

export interface PlatformSettings {
  /** The desk may read `/admin/settings`; without it every value is unknown. */
  readable: boolean
  /** The list has arrived (or will never be asked for). */
  settled: boolean
  /** The stored or default value; `undefined` when unknown. */
  get: (key: string) => unknown
}

/**
 * The same list the Settings screen shows, read by screens that change with a
 * setting (copy trading, signals). Only desks with `settings` may read it; the
 * others get `undefined` and fall back to the defaults the spec names.
 */
export function usePlatformSettings(): PlatformSettings {
  // App reads settings before sign-in too (the nav); asking for `me` then would only earn a 401.
  const me = useQuery({ queryKey: ['me'], queryFn: getCurrentUser, staleTime: 60_000, enabled: isAuthenticated(), retry: false })
  const role = me.data?.user.role
  const readable = role !== undefined && hasPermission(role, 'settings')
  const list = useQuery({ queryKey: ['settings'], queryFn: getSettings, enabled: readable, staleTime: 30_000, retry: false })
  const values = new Map((list.data ?? []).map((row) => [row.key, row.value] as const))
  return {
    readable,
    settled: me.isFetched && (!readable || list.isFetched),
    get: (key) => values.get(key),
  }
}

/**
 * `copy.enabled`: copy trading is off by default (pivot of 28 September). Only
 * an explicit `true` shows the masters screen, tiles and report panel. A desk
 * that owns masters but cannot read settings (content) asks the gated route
 * itself: while the flag is off it answers FEATURE_DISABLED.
 */
export function useCopyState(): { enabled: boolean; settled: boolean } {
  const settings = usePlatformSettings()
  const me = useQuery({ queryKey: ['me'], queryFn: getCurrentUser, staleTime: 60_000, enabled: isAuthenticated(), retry: false })
  const role = me.data?.user.role
  const probing = settings.settled && !settings.readable && role !== undefined && hasPermission(role, 'masters')
  const probe = useQuery({
    queryKey: ['copy-enabled-probe'],
    queryFn: () =>
      getMasters().then(
        () => true,
        (error: unknown) => (error instanceof ApiError && error.code === 'FEATURE_DISABLED' ? false : Promise.reject(error)),
      ),
    enabled: probing,
    staleTime: 60_000,
    retry: false,
  })
  if (settings.readable) {
    const value = settings.get('copy.enabled')
    return { enabled: value === true || value === 'true', settled: settings.settled }
  }
  return { enabled: probe.data === true, settled: settings.settled && (!probing || probe.isFetched) }
}

export function useCopyEnabled(): boolean {
  return useCopyState().enabled
}

/**
 * `signals.code_ttl_minutes` when it is unset or unreadable: a code lives five
 * minutes unless its signal is given a length of its own (28 and 30 September).
 */
export const SIGNAL_CODE_TTL_DEFAULT = 5

/**
 * Whole minutes 1…1440 — a day — read the way the API reads them, as a number
 * or a numeric string: the default in `signals.code_ttl_minutes` and one
 * signal's own window alike. Null for anything else — the API would quietly
 * fall back to 5 or refuse the signal, so the desk refuses it up front.
 */
export function parseSignalCodeTtl(raw: unknown): number | null {
  const text = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : ''
  if (!/^\d+$/.test(text)) return null
  const minutes = Number(text)
  return minutes >= 1 && minutes <= 1440 ? minutes : null
}

/** The default length of a signal code, in minutes: `/platform/config` `signalCodeTtlMinutes`, else 5. */
export function signalCodeTtlMinutes(raw: unknown): number {
  return parseSignalCodeTtl(raw) ?? SIGNAL_CODE_TTL_DEFAULT
}

/** The lengths the signal form offers in one tap; any other up to a day is typed in. */
export const SIGNAL_WINDOW_PRESETS = [5, 15, 30, 60, 120] as const

/** «5 мин», «1 ч», «1 ч 30 мин», «24 ч»: a code window's length as the desk says it. */
export function windowLengthLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} мин`
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`
}
